/**
 * Bangladesh public holidays.
 * Fixed-date holidays are exact; lunar/variable dates are approximate and
 * may shift ±1 day depending on moon sighting / final NBR notification.
 */
export interface Holiday {
  date: string; // ISO yyyy-mm-dd
  name: { en: string; bn: string };
  type: "national" | "religious" | "cultural";
  approximate?: boolean;
}

function fixed(year: number): Holiday[] {
  return [
    { date: `${year}-02-21`, name: { en: "International Mother Language Day", bn: "আন্তর্জাতিক মাতৃভাষা দিবস" }, type: "national" },
    { date: `${year}-03-17`, name: { en: "Birthday of Bangabandhu / Children's Day", bn: "বঙ্গবন্ধুর জন্মদিন / শিশু দিবস" }, type: "national" },
    { date: `${year}-03-26`, name: { en: "Independence Day", bn: "স্বাধীনতা দিবস" }, type: "national" },
    { date: `${year}-04-14`, name: { en: "Pohela Boishakh (Bengali New Year)", bn: "পহেলা বৈশাখ" }, type: "cultural" },
    { date: `${year}-05-01`, name: { en: "May Day", bn: "মে দিবস" }, type: "national" },
    { date: `${year}-08-15`, name: { en: "National Mourning Day", bn: "জাতীয় শোক দিবস" }, type: "national" },
    { date: `${year}-12-16`, name: { en: "Victory Day", bn: "বিজয় দিবস" }, type: "national" },
    { date: `${year}-12-25`, name: { en: "Christmas Day", bn: "বড়দিন" }, type: "religious" },
    { date: `${year}-02-13`, name: { en: "Pohela Falgun", bn: "পহেলা ফাল্গুন" }, type: "cultural" },
  ];
}

// Approximate religious holiday dates by year. Update annually per NBR.
const VARIABLE: Record<number, Holiday[]> = {
  2025: [
    { date: "2025-02-14", name: { en: "Shab-e-Barat", bn: "শব-ই-বরাত" }, type: "religious", approximate: true },
    { date: "2025-03-27", name: { en: "Shab-e-Qadr", bn: "শব-ই-কদর" }, type: "religious", approximate: true },
    { date: "2025-03-30", name: { en: "Eid-ul-Fitr (Day 1)", bn: "ঈদুল ফিতর (১ম দিন)" }, type: "religious", approximate: true },
    { date: "2025-03-31", name: { en: "Eid-ul-Fitr (Day 2)", bn: "ঈদুল ফিতর (২য় দিন)" }, type: "religious", approximate: true },
    { date: "2025-04-01", name: { en: "Eid-ul-Fitr (Day 3)", bn: "ঈদুল ফিতর (৩য় দিন)" }, type: "religious", approximate: true },
    { date: "2025-05-12", name: { en: "Buddha Purnima", bn: "বুদ্ধ পূর্ণিমা" }, type: "religious", approximate: true },
    { date: "2025-06-06", name: { en: "Eid-ul-Adha (Day 1)", bn: "ঈদুল আজহা (১ম দিন)" }, type: "religious", approximate: true },
    { date: "2025-06-07", name: { en: "Eid-ul-Adha (Day 2)", bn: "ঈদুল আজহা (২য় দিন)" }, type: "religious", approximate: true },
    { date: "2025-06-08", name: { en: "Eid-ul-Adha (Day 3)", bn: "ঈদুল আজহা (৩য় দিন)" }, type: "religious", approximate: true },
    { date: "2025-07-06", name: { en: "Ashura", bn: "আশুরা" }, type: "religious", approximate: true },
    { date: "2025-09-05", name: { en: "Eid-e-Miladunnabi", bn: "ঈদে মিলাদুন্নবী" }, type: "religious", approximate: true },
    { date: "2025-10-02", name: { en: "Durga Puja (Vijaya Dashami)", bn: "দুর্গা পূজা (বিজয়া দশমী)" }, type: "religious", approximate: true },
  ],
  2026: [
    { date: "2026-02-03", name: { en: "Shab-e-Barat", bn: "শব-ই-বরাত" }, type: "religious", approximate: true },
    { date: "2026-03-16", name: { en: "Shab-e-Qadr", bn: "শব-ই-কদর" }, type: "religious", approximate: true },
    { date: "2026-03-20", name: { en: "Eid-ul-Fitr (Day 1)", bn: "ঈদুল ফিতর (১ম দিন)" }, type: "religious", approximate: true },
    { date: "2026-03-21", name: { en: "Eid-ul-Fitr (Day 2)", bn: "ঈদুল ফিতর (২য় দিন)" }, type: "religious", approximate: true },
    { date: "2026-03-22", name: { en: "Eid-ul-Fitr (Day 3)", bn: "ঈদুল ফিতর (৩য় দিন)" }, type: "religious", approximate: true },
    { date: "2026-05-01", name: { en: "Buddha Purnima", bn: "বুদ্ধ পূর্ণিমা" }, type: "religious", approximate: true },
    { date: "2026-05-27", name: { en: "Eid-ul-Adha (Day 1)", bn: "ঈদুল আজহা (১ম দিন)" }, type: "religious", approximate: true },
    { date: "2026-05-28", name: { en: "Eid-ul-Adha (Day 2)", bn: "ঈদুল আজহা (২য় দিন)" }, type: "religious", approximate: true },
    { date: "2026-05-29", name: { en: "Eid-ul-Adha (Day 3)", bn: "ঈদুল আজহা (৩য় দিন)" }, type: "religious", approximate: true },
    { date: "2026-06-26", name: { en: "Ashura", bn: "আশুরা" }, type: "religious", approximate: true },
    { date: "2026-08-26", name: { en: "Eid-e-Miladunnabi", bn: "ঈদে মিলাদুন্নবী" }, type: "religious", approximate: true },
    { date: "2026-10-21", name: { en: "Durga Puja (Vijaya Dashami)", bn: "দুর্গা পূজা (বিজয়া দশমী)" }, type: "religious", approximate: true },
  ],
  2027: [
    { date: "2027-01-23", name: { en: "Shab-e-Barat", bn: "শব-ই-বরাত" }, type: "religious", approximate: true },
    { date: "2027-03-09", name: { en: "Eid-ul-Fitr (Day 1)", bn: "ঈদুল ফিতর (১ম দিন)" }, type: "religious", approximate: true },
    { date: "2027-03-10", name: { en: "Eid-ul-Fitr (Day 2)", bn: "ঈদুল ফিতর (২য় দিন)" }, type: "religious", approximate: true },
    { date: "2027-05-17", name: { en: "Eid-ul-Adha (Day 1)", bn: "ঈদুল আজহা (১ম দিন)" }, type: "religious", approximate: true },
    { date: "2027-05-18", name: { en: "Eid-ul-Adha (Day 2)", bn: "ঈদুল আজহা (২য় দিন)" }, type: "religious", approximate: true },
  ],
};

export function getBdHolidays(year: number): Holiday[] {
  const all = [...fixed(year), ...(VARIABLE[year] ?? [])];
  return all.sort((a, b) => a.date.localeCompare(b.date));
}

export function isHoliday(date: Date, holidays: Holiday[]): Holiday | null {
  const iso = date.toISOString().slice(0, 10);
  return holidays.find((h) => h.date === iso) ?? null;
}
